/**
 * Centralized logging system for the application
 */

export enum LogLevel {
  DEBUG = 0,
  INFO = 1,
  WARN = 2,
  ERROR = 3
}

interface LogEntry {
  level: LogLevel;
  message: string;
  data?: any;
  timestamp: Date;
  component?: string;
  userId?: string;
}

class Logger {
  private logLevel: LogLevel = import.meta.env.DEV ? LogLevel.DEBUG : LogLevel.INFO;
  private logs: LogEntry[] = [];
  private maxLogs = 1000;
  private currentUserId?: string;

  private shouldLog(level: LogLevel): boolean {
    return level >= this.logLevel;
  }

  private formatMessage(level: LogLevel, message: string, component?: string): string {
    const levelStr = LogLevel[level];
    const componentStr = component ? `[${component}]` : '';
    return `${levelStr}${componentStr}: ${message}`;
  }

  private addLog(level: LogLevel, message: string, data?: any, component?: string) {
    const entry: LogEntry = {
      level,
      message,
      data,
      timestamp: new Date(),
      component,
      userId: this.getCurrentUserId()
    };

    this.logs.push(entry);
    
    // Keep only the most recent logs
    if (this.logs.length > this.maxLogs) {
      this.logs = this.logs.slice(-this.maxLogs);
    }

    // Console output for development
    if (import.meta.env.DEV) {
      const formattedMessage = this.formatMessage(level, message, component);
      
      switch (level) {
        case LogLevel.DEBUG:
          console.debug(formattedMessage, data);
          break;
        case LogLevel.INFO:
          console.info(formattedMessage, data);
          break;
        case LogLevel.WARN:
          console.warn(formattedMessage, data);
          break;
        case LogLevel.ERROR:
          console.error(formattedMessage, data);
          break;
      }
    }
  }

  private getCurrentUserId(): string | undefined {
    return this.currentUserId;
  }

  debug(message: string, data?: any, component?: string) {
    if (this.shouldLog(LogLevel.DEBUG)) {
      this.addLog(LogLevel.DEBUG, message, data, component);
    }
  }

  info(message: string, data?: any, component?: string) {
    if (this.shouldLog(LogLevel.INFO)) {
      this.addLog(LogLevel.INFO, message, data, component);
    }
  }

  warn(message: string, data?: any, component?: string) {
    if (this.shouldLog(LogLevel.WARN)) {
      this.addLog(LogLevel.WARN, message, data, component);
    }
  }

  error(message: string, data?: any, component?: string) {
    if (this.shouldLog(LogLevel.ERROR)) {
      this.addLog(LogLevel.ERROR, message, data, component);
    }
  }

  // Get logs for debugging or sending to error tracking service
  getLogs(level?: LogLevel): LogEntry[] {
    if (level !== undefined) {
      return this.logs.filter(log => log.level >= level);
    }
    return [...this.logs];
  }

  // Clear logs
  clearLogs() {
    this.logs = [];
  }

  // Set log level
  setLogLevel(level: LogLevel) {
    this.logLevel = level;
  }

  // Cache user id to avoid repeated localStorage parsing
  setUserId(userId?: string) {
    this.currentUserId = userId;
  }
}

// Export singleton instance
export const logger = new Logger();

// Convenience functions
export const log = {
  debug: (message: string, data?: any, component?: string) => logger.debug(message, data, component),
  info: (message: string, data?: any, component?: string) => logger.info(message, data, component),
  warn: (message: string, data?: any, component?: string) => logger.warn(message, data, component),
  error: (message: string, data?: any, component?: string) => logger.error(message, data, component),
};
